import { Fragment, useEffect, useMemo, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiError } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { daftarJenis, jenisBawaan, JENIS_PESAN, linkWa, nomorWa, susunPesan, tampilNomor } from '../lib/pesanWa';

// Dialog "Kirim WhatsApp" — SATU-SATUNYA pintu pesan ke customer (31 Okt 2026).
// Menggantikan tautan wa.me yang tersebar di 6 halaman dengan isi pesan berbeda-beda.
//   - nomor selalu diambil TERBARU dari kartu customer (GET /api/wa/kontak)
//   - jenis pesan dipilih otomatis sesuai keadaan order, bisa diganti
//   - teks bisa diubah sebelum dikirim; pesan tidak pernah terkirim otomatis
//
// target:
//   { kind: 'order' | 'booking', id }      -> data & nomor diambil dari server
//   { kind: 'customer', name, whatsapp }   -> sapaan singkat

let cachePengaturan = null;
async function ambilPengaturan() {
  if (cachePengaturan) return cachePengaturan;
  try {
    const r = await api.get('/settings');
    cachePengaturan = r.data || {};
  } catch {
    cachePengaturan = {};
  }
  return cachePengaturan;
}

// *tebal* ala WhatsApp, tanpa dangerouslySetInnerHTML.
function Gelembung({ teks }) {
  const potong = String(teks || '').split(/(\*[^*\n]+\*)/g);
  return (
    <>
      {potong.map((p, i) => (/^\*[^*\n]+\*$/.test(p)
        ? <b key={i}>{p.slice(1, -1)}</b>
        : <Fragment key={i}>{p}</Fragment>))}
    </>
  );
}

async function salin(teks) {
  try {
    await navigator.clipboard.writeText(teks);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = teks;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    document.body.removeChild(ta);
    return ok;
  }
}

export function KirimWaDialog({ open, onClose, target }) {
  const [memuat, setMemuat] = useState(false);
  const [galat, setGalat] = useState('');
  const [kontak, setKontak] = useState(null);   // { data, wa, beda, waOrder }
  const [pengaturan, setPengaturan] = useState({});
  const [jenis, setJenis] = useState('sapa');
  const [teks, setTeks] = useState('');

  const kunciTarget = target ? `${target.kind}|${target.id || target.whatsapp || ''}` : '';

  useEffect(() => {
    if (!open || !target) return undefined;
    let batal = false;
    setGalat('');
    setKontak(null);
    setMemuat(true);
    (async () => {
      const set = await ambilPengaturan();
      if (batal) return;
      setPengaturan(set);
      if (target.kind === 'customer') {
        setKontak({ data: { customer_name: target.name }, wa: target.whatsapp || '', beda: false, waOrder: '' });
        setMemuat(false);
        return;
      }
      try {
        const r = await api.get('/wa/kontak', { params: { jenis: target.kind, id: target.id } });
        if (batal) return;
        setKontak({ data: r.data.data, wa: r.data.wa, beda: r.data.beda, waOrder: r.data.wa_order });
      } catch (e) {
        if (!batal) setGalat(apiError(e, 'Data order tidak bisa dimuat'));
      } finally {
        if (!batal) setMemuat(false);
      }
    })();
    return () => { batal = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kunciTarget]);

  const data = kontak?.data || null;
  const pilihan = useMemo(() => (target?.kind === 'customer' ? ['sapa'] : daftarJenis(data)), [data, target?.kind]);

  // Pesan awal dipilih sekali per kali dialog dibuka / data selesai dimuat.
  useEffect(() => {
    if (!kontak) return;
    const j = target?.kind === 'customer' ? 'sapa' : (target?.jenis && pilihan.includes(target.jenis) ? target.jenis : jenisBawaan(data));
    setJenis(j);
    setTeks(susunPesan(j, data, pengaturan));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kontak]);

  const ganti = (j) => {
    setJenis(j);
    setTeks(susunPesan(j, data, pengaturan));
  };

  const nomor = nomorWa(kontak?.wa);
  const awal = kontak ? susunPesan(jenis, data, pengaturan) : '';
  const diubah = teks !== awal;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="wa-dialog">
        <DialogHeader>
          <DialogTitle>Kirim WhatsApp</DialogTitle>
        </DialogHeader>

        {memuat && <p className="text-sm text-mocha py-6 text-center" data-testid="wa-memuat">Memuat…</p>}
        {galat && <p className="text-sm text-danger py-4" data-testid="wa-galat">{galat}</p>}

        {kontak && !memuat && (
          <div className="space-y-3">
            <div className="text-sm" data-testid="wa-kepada">
              Kepada: <b>{data?.customer_name || '-'}</b>
              {' · '}
              {nomor.nomor
                ? <span data-testid="wa-nomor">{tampilNomor(nomor.nomor)}</span>
                : <span className="text-danger" data-testid="wa-nomor-salah">nomor tidak bisa dipakai</span>}
            </div>

            <div data-testid="wa-peringatan" className="space-y-1.5">
              {kontak.beda && (
                <p className="text-xs rounded-lg bg-[#EAF2FB] text-[#255A8F] px-3 py-2" data-testid="wa-info-beda">
                  Memakai nomor terbaru di kartu customer. Nomor yang tersimpan di order: {kontak.waOrder}.
                </p>
              )}
              {nomor.status === 'beberapa' && (
                <p className="text-xs rounded-lg bg-warning/15 text-[#8a5f1e] px-3 py-2" data-testid="wa-info-beberapa">
                  Kolom WhatsApp berisi lebih dari satu nomor. Yang dipakai: nomor pertama. Rapikan di menu Customer.
                </p>
              )}
              {(nomor.status === 'salah' || nomor.status === 'kosong') && (
                <p className="text-xs rounded-lg bg-danger/10 text-danger px-3 py-2" data-testid="wa-info-salah">
                  {nomor.status === 'kosong' ? 'Customer ini belum punya nomor WhatsApp.' : `Nomor "${nomor.asli}" tidak valid.`}
                  {' '}Betulkan dulu di menu Customer. Teks tetap bisa disalin.
                </p>
              )}
            </div>

            {pilihan.length > 1 && (
              <div className="flex flex-wrap gap-1.5" data-testid="wa-jenis">
                {pilihan.map((j) => (
                  <button
                    key={j}
                    type="button"
                    data-testid={`wa-jenis-${j}`}
                    onClick={() => ganti(j)}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${jenis === j ? 'bg-brown text-white' : 'bg-white border border-border text-mocha hover:bg-ivory'}`}
                  >
                    {JENIS_PESAN[j]}
                  </button>
                ))}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-mocha font-semibold mb-1.5">Teks (bisa diubah)</p>
                <textarea
                  data-testid="wa-teks"
                  value={teks}
                  onChange={(e) => setTeks(e.target.value)}
                  className="w-full min-h-[300px] rounded-xl border border-input bg-ivory/50 px-3 py-2.5 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-brown/30"
                />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-mocha font-semibold mb-1.5">Tampilan di WhatsApp customer</p>
                <div className="rounded-xl bg-[#E5DDD5] p-3 min-h-[300px]">
                  <div data-testid="wa-bubble" className="ml-6 rounded-xl rounded-br-sm bg-[#DCF8C6] px-3 py-2 text-[13.5px] leading-relaxed whitespace-pre-wrap break-words">
                    <Gelembung teks={teks} />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2 pt-1">
              {diubah && (
                <button type="button" data-testid="wa-ulang" onClick={() => setTeks(awal)}
                  className="rounded-xl border border-border px-4 py-2.5 text-sm font-semibold text-mocha hover:bg-ivory">
                  Kembalikan teks awal
                </button>
              )}
              <button type="button" data-testid="wa-salin"
                onClick={async () => {
                  if (await salin(teks)) toast.success('Teks disalin');
                  else toast.error('Tidak bisa menyalin. Salin manual dari kotak teks.');
                }}
                className="rounded-xl border border-border px-4 py-2.5 text-sm font-semibold text-brown hover:bg-ivory">
                Salin teks
              </button>
              <button type="button" data-testid="wa-lewati" onClick={() => onClose?.()}
                className="rounded-xl border border-border px-4 py-2.5 text-sm font-semibold text-mocha hover:bg-ivory">
                Tutup
              </button>
              {nomor.nomor ? (
                <a
                  data-testid="wa-buka"
                  href={linkWa(nomor.nomor, teks)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setTimeout(() => onClose?.(), 300)}
                  className="rounded-xl bg-[#1da851] text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90"
                >
                  Buka WhatsApp
                </a>
              ) : (
                <button type="button" data-testid="wa-buka" disabled className="rounded-xl bg-[#1da851] text-white px-5 py-2.5 text-sm font-semibold opacity-40 cursor-not-allowed">
                  Buka WhatsApp
                </button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Tombol ikon pesan yang membuka dialog. Dipakai di Order Sewa, Detail, Nota,
// Pemantauan, Booking, dan Customer.
export function TombolWa({ target, testid, title = 'Kirim WhatsApp', className = '', ikon = 15, children }) {
  const [buka, setBuka] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid={testid}
        title={title}
        onClick={(e) => { e.stopPropagation(); setBuka(true); }}
        className={className}
      >
        {children || <MessageCircle size={ikon} />}
      </button>
      {buka && <KirimWaDialog open={buka} onClose={() => setBuka(false)} target={target} />}
    </>
  );
}
